import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import toast from 'react-hot-toast'
import { useTradingHistoryStore } from './tradingHistoryStore'
import { portfolioHistoryService } from '../services/portfolioHistoryService'
import { apiFetch } from '../lib/api'

// Types
export interface Holding {
  id: number
  symbol: string
  name: string
  port_api_type: 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold'
  sector?: string
  country?: string
  broker?: string
  shares: number
  purchase_price: number
  purchase_currency: 'USD' | 'THB'
  fund_class?: string
  project_id?: string
  portfolio_id?: number | null
  created_at: string
  updated_at: string
}

export interface PriceData {
  usd: number
  thb: number
  exchangeRate: number
  baseCurrency: string
  timestamp: string
}

export interface PortfolioSummary {
  totalValue: number
  totalCost: number
  totalGainLoss: number
  gainLossPercent: number
  realizedPnL: number
}

export interface CustomPortfolio {
  id: number
  name: string
  description: string
  icon: string
  color: string
  currency: 'USD' | 'THB'
  port_type?: string
  port_api_type?: 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold'
  is_default: boolean
  created_at: string
  updated_at: string
}

export interface Sector {
  id: number
  key: string
  label: string
  icon: string
  color: string
  bg_color: string
  text_color: string
  border_color: string
  created_at: string
  updated_at: string
}

export interface Currency {
  id: number
  value: string
  label: string
  icon: string
  symbol: string
  created_at: string
  updated_at: string
}

export interface Country {
  id: number
  key: string
  label: string
  icon: string
  color: string
  bg_color: string
  text_color: string
  border_color: string
  created_at: string
  updated_at: string
}

interface PortfolioState {
  // Data
  portfolio: Holding[]
  currentPrices: Record<string, PriceData>
  exchangeRate: number
  displayCurrency: 'USD' | 'THB'
  
  // Custom Portfolio Data
  customPortfolios: CustomPortfolio[]
  selectedPortfolioId: number | null
  portfolioHoldings: Record<number, Holding[]>
  loadingPortfolios: Set<number> // Track which portfolios are currently being loaded

  // Sector Data
  sectors: Sector[]

  // Country Data
  countries: Country[]

  // Currency Data
  currencies: Currency[]
  
  // UI State
  loading: boolean
  updating: boolean
  loadingPortfolio: boolean // Track if portfolio is being loaded
  loadingPrices: boolean // Track if prices are being loaded
  loadingExchangeRate: boolean // Track if exchange rate is being loaded
  loadingCustomPortfolios: boolean // Track if custom portfolios are being loaded
  customPortfoliosLoaded: boolean // Track if custom portfolios have ever been loaded
  loadingCurrencies: boolean
  currenciesLoaded: boolean
  loadingSectors: boolean
  sectorsLoaded: boolean
  loadingCountries: boolean
  countriesLoaded: boolean
  showAddModal: boolean
  showSellModal: boolean
  showDeleteModal: boolean
  showCustomPortfolioModal: boolean
  showAddGoldModal: boolean
  showCsvImportModal: boolean
  selectedStock: Holding | null
  selectedPortfolio: CustomPortfolio | null
  
  // Actions
  loadPortfolio: () => Promise<void>
  loadLatestPrices: () => Promise<void>
  loadExchangeRate: () => Promise<void>
  fetchAllPrices: () => Promise<void>
  fetchGoldPrice: () => Promise<number>
  addHolding: (
    holding: Omit<Holding, 'id' | 'created_at' | 'updated_at'> & {
      created_at?: string
      portfolio_id?: number
      recordTradingHistory?: boolean
    }
  ) => Promise<{ message: string; id: number }>
  updateHolding: (
    holdingId: number,
    holdingData: Omit<Holding, 'id' | 'created_at' | 'updated_at'> & {
      created_at?: string
      portfolio_id?: number
      recordTradingHistory?: boolean
    }
  ) => Promise<void>
  sellHolding: (symbol: string, sharesToSell: number, customPrice?: number, holdingData?: {
    name: string; port_api_type: string; purchase_currency: string; created_at?: string; portfolio_id?: number; broker?: string
  }) => Promise<void>
  deleteHolding: (symbol: string) => Promise<void>
  setDisplayCurrency: (currency: 'USD' | 'THB') => void
  openSellModal: (stock: Holding) => void
  openDeleteModal: (stock: Holding) => void
  openAddGoldModal: () => void
  closeAddGoldModal: () => void
  openCsvImportModal: () => void
  closeCsvImportModal: () => void
  
  // Custom Portfolio Actions
  loadCustomPortfolios: () => Promise<void>
  loadPortfolioHoldings: (portfolioId: number, force?: boolean) => Promise<void>
  createCustomPortfolio: (portfolio: Omit<CustomPortfolio, 'id' | 'created_at' | 'updated_at' | 'is_default'>) => Promise<void>
  updateCustomPortfolio: (id: number, portfolio: Partial<CustomPortfolio>) => Promise<void>
  deleteCustomPortfolio: (id: number) => Promise<void>
  assignHoldingToPortfolio: (portfolioId: number, holdingId: number) => Promise<void>
  removeHoldingFromPortfolio: (portfolioId: number, holdingId: number) => Promise<void>
  autoAssignHoldings: () => Promise<void>
  setSelectedPortfolio: (portfolioId: number | null) => void
  openCustomPortfolioModal: (portfolio?: CustomPortfolio) => void
  
  // Sector Actions
  loadSectors: () => Promise<void>
  createSector: (sector: Omit<Sector, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateSector: (id: number, sector: Partial<Sector>) => Promise<void>
  deleteSector: (id: number) => Promise<void>
  getSectorByKey: (key: string) => Sector | undefined

  // Country Actions
  loadCountries: () => Promise<void>
  createCountry: (country: Omit<Country, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateCountry: (id: number, country: Partial<Country>) => Promise<void>
  deleteCountry: (id: number) => Promise<void>
  getCountryByKey: (key: string) => Country | undefined

  // Currency Actions
  loadCurrencies: () => Promise<void>
  createCurrency: (currency: Omit<Currency, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateCurrency: (id: number, currency: Partial<Currency>) => Promise<void>
  deleteCurrency: (id: number) => Promise<void>
  getCurrencyByValue: (value: string) => Currency | undefined
  
  // Computed
  getCurrentPrice: (symbol: string, currency?: 'USD' | 'THB') => number
  convertPrice: (price: number, fromCurrency: string, toCurrency: string) => number
  calculateRealizedPnL: () => number
  calculateSummary: () => PortfolioSummary
  getFilteredPortfolio: () => Holding[]
  getPortfolioCurrency: () => 'USD' | 'THB'
  detectCurrencyFromHoldings: (portfolioId: number) => 'USD' | 'THB'
  getPortfolioStats: (portfolioId: number) => { totalStocks: number; totalValue: number; totalCost: number; gainLoss: number; gainLossPercent: number }
  updateHoldingSector: (holdingId: number, sector: string) => Promise<void>
  getAvailableSectors: () => string[]
  clearDatabase: () => Promise<void>
  triggerDailySnapshot: () => Promise<void>
}

export const usePortfolioStore = create<PortfolioState>()(
  devtools(
    (set, get) => ({
      // Initial state
      portfolio: [],
      currentPrices: {},
      exchangeRate: 0.0,
      displayCurrency: 'THB',
      
      // Custom Portfolio Initial State
      customPortfolios: [],
      selectedPortfolioId: null,
      portfolioHoldings: {},
      loadingPortfolios: new Set(),

      // Sector Initial State
      sectors: [],

      // Country Initial State
      countries: [],

      // Currency Initial State
      currencies: [],
      
      // Starts true (rather than false) so the very first render after a hard
      // refresh doesn't read "loading: false, portfolio: []" and briefly flash
      // the empty-state screen before loadPortfolio() has even been called.
      loading: true,
      updating: false,
      loadingPortfolio: false,
      loadingPrices: false,
      loadingExchangeRate: false,
      loadingCustomPortfolios: false,
      customPortfoliosLoaded: false,
      currenciesLoaded: false,
      sectorsLoaded: false,
      countriesLoaded: false,
      loadingCurrencies: false,
      loadingSectors: false,
      loadingCountries: false,
      showAddModal: false,
      showSellModal: false,
      showDeleteModal: false,
      showCustomPortfolioModal: false,
      showAddGoldModal: false,
      showCsvImportModal: false,
      selectedStock: null,
      selectedPortfolio: null,

      // Actions
      loadPortfolio: async () => {
        // Check if already loading
        const { loadingPortfolio } = get()
        if (loadingPortfolio) {
          console.log('⏭️  Skipping duplicate portfolio load')
          return
        }

        set({ loadingPortfolio: true, loading: true })

        try {
          console.log('💼 Loading portfolio...')
          const response = await apiFetch(`/api/portfolio`)
          if (!response.ok) throw new Error('Failed to fetch portfolio')
          const data = await response.json()
          set({ portfolio: data })
          console.log('✅ Loaded portfolio:', data?.length, 'holdings')
        } catch (error) {
          console.error('Error loading portfolio:', error)
          toast.error('Failed to load portfolio')
        } finally {
          set({ loadingPortfolio: false, loading: false })
        }
      },

      loadLatestPrices: async () => {
        // Check if already loading
        const { loadingPrices } = get()
        if (loadingPrices) {
          console.log('⏭️  Skipping duplicate prices load')
          return
        }

        set({ loadingPrices: true })

        try {
          console.log('💰 Loading latest prices...')
          const response = await apiFetch(`/api/prices/latest`)
          if (!response.ok) throw new Error('Failed to fetch prices')
          const data = await response.json()
          set({ currentPrices: data })
          console.log('✅ Loaded prices for', Object.keys(data).length, 'symbols')

          // Check if we have any gold holdings and fetch current gold price
          const { portfolio } = get()
          const hasGoldHoldings = portfolio.some(h => h.port_api_type === 'gold')
          if (hasGoldHoldings) {
            try {
              await get().fetchGoldPrice()
              console.log('✓ Gold price updated')
            } catch (error) {
              console.log('⚠ Failed to update gold price, using fallback')
            }
          }

        } catch (error) {
          console.error('Error loading prices:', error)
        } finally {
          set({ loadingPrices: false })
        }
      },

      loadExchangeRate: async () => {
        // Check if already loading
        const { loadingExchangeRate } = get()
        if (loadingExchangeRate) {
          console.log('⏭️  Skipping duplicate exchange rate load')
          return
        }

        set({ loadingExchangeRate: true })

        try {
          console.log('💱 Loading exchange rate...')
          const response = await apiFetch(`/api/exchange-rate`)
          if (!response.ok) throw new Error('Failed to fetch exchange rate')
          const data = await response.json()
          set({ exchangeRate: data.rate })
          console.log('✅ Loaded exchange rate:', data.rate)
        } catch (error) {
          console.error('Error loading exchange rate:', error)
        } finally {
          set({ loadingExchangeRate: false })
        }
      },

      fetchAllPrices: async () => {
        const { portfolio } = get()
        if (portfolio.length === 0) {
          toast.error('No stocks in portfolio')
          return
        }

        try {
          set({ updating: true })
          toast.loading('Updating prices...', { id: 'price-update' })
          
          const response = await apiFetch(`/api/prices/update`, {
            method: 'POST'
          })
          
          if (!response.ok) throw new Error('Failed to update prices')
          
          const data = await response.json()
          
          // Reload latest prices and exchange rate
          await Promise.all([
            get().loadLatestPrices(),
            get().loadExchangeRate()
          ])
          
          const successCount = data.results.filter((r: any) => r.success).length
          const failCount = data.results.filter((r: any) => !r.success).length
          
          toast.success(
            `Updated ${successCount} stocks${failCount > 0 ? ` (${failCount} failed)` : ''}`,
            { id: 'price-update' }
          )
          
          await get().triggerDailySnapshot()
        } catch (error) {
          console.error('Error updating prices:', error)
          toast.error('Failed to update prices', { id: 'price-update' })
        } finally {
          set({ updating: false })
        }
      },

      addHolding: async (holding) => {
        try {
          const response = await apiFetch(`/api/portfolio`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...holding,
              symbol: holding.symbol.toUpperCase()
            })
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to add holding')
          }

          const responseData = await response.json()

          if (holding.recordTradingHistory !== false) {
            await useTradingHistoryStore.getState().addTradingRecord({
              symbol: holding.symbol.toUpperCase(),
              name: holding.name,
              port_api_type: holding.port_api_type,
              action: 'BUY',
              shares: holding.shares,
              price: holding.purchase_price,
              currency: holding.purchase_currency,
              exchange_rate: get().exchangeRate,
              total_amount: holding.purchase_price * holding.shares,
              notes: 'Stock purchase',
              created_at: holding.created_at,
              portfolio_id: holding.portfolio_id
            })
          }

          await get().loadPortfolio()
          toast.success('Stock added successfully')
          set({ showAddModal: false })
          
          return responseData
        } catch (error: any) {
          console.error('Error adding holding:', error)
          toast.error(error.message || 'Failed to add stock')
          throw error
        }
      },

      updateHolding: async (holdingId, holdingData) => {
        try {
          // First, get the current holding data to compare changes
          const currentHolding = get().portfolio.find(h => h.id === holdingId)
          if (!currentHolding) {
            throw new Error('Holding not found')
          }

          const response = await apiFetch(`/api/portfolio/${holdingId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...holdingData,
              symbol: holdingData.symbol.toUpperCase(),
              holdingId: holdingId
            })
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to update holding')
          }

          // If shares or price changed, add to trading history
          const sharesChanged = holdingData.shares !== currentHolding.shares
          const priceChanged = holdingData.purchase_price !== currentHolding.purchase_price

          if (sharesChanged || priceChanged) {
            const sharesDiff = holdingData.shares - currentHolding.shares
            
            if (sharesDiff !== 0) {
              await useTradingHistoryStore.getState().addTradingRecord({
                symbol: holdingData.symbol.toUpperCase(),
                name: holdingData.name || holdingData.symbol,
                port_api_type: holdingData.port_api_type,
                action: sharesDiff > 0 ? 'BUY' : 'SELL',
                shares: Math.abs(sharesDiff),
                price: holdingData.purchase_price,
                currency: holdingData.purchase_currency,
                exchange_rate: get().exchangeRate,
                total_amount: holdingData.purchase_price * Math.abs(sharesDiff),
                notes: 'Holding adjustment via edit',
                broker: holdingData.broker,
                created_at: holdingData.created_at,
                portfolio_id: holdingData.portfolio_id
              })
            }
          }

          await get().loadPortfolio()
          toast.success('Holding updated successfully')
        } catch (error: any) {
          console.error('Error updating holding:', error)
          toast.error(error.message || 'Failed to update holding')
          throw error
        }
      },

      sellHolding: async (symbol, sharesToSell, customPrice, holdingData) => {
        try {
          const { selectedStock, exchangeRate, getCurrentPrice } = get()

          const response = await apiFetch(`/api/portfolio/${symbol}/sell`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shares_to_sell: sharesToSell })
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to sell stock')
          }

          // Add to trading history
          const stockInfo = holdingData || selectedStock
          if (stockInfo) {
            const getSellCurrency = (portApiType: string) => {
              switch (portApiType) {
                case 'us_stock':
                case 'us_fund':
                  return 'USD'
                case 'th_stock':
                case 'th_fund':
                case 'gold':
                  return 'THB'
                default:
                  return stockInfo.purchase_currency
              }
            }

            const sellCurrency = getSellCurrency(stockInfo.port_api_type) as 'USD' | 'THB'
            const sellPrice = customPrice !== undefined ? customPrice : getCurrentPrice(symbol, sellCurrency)

            await useTradingHistoryStore.getState().addTradingRecord({
              symbol,
              name: stockInfo.name,
              port_api_type: stockInfo.port_api_type as 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold',
              action: 'SELL',
              shares: sharesToSell,
              price: sellPrice,
              currency: sellCurrency,
              exchange_rate: exchangeRate,
              total_amount: sellPrice * sharesToSell,
              notes: customPrice !== undefined ? 'Stock sale (custom price)' : 'Stock sale',
              broker: holdingData?.broker,
              created_at: stockInfo.created_at,
              portfolio_id: stockInfo.portfolio_id
            })
          }

          await get().loadPortfolio()
          toast.success('Stock sold successfully')
          if (!holdingData) {
            set({ showSellModal: false, selectedStock: null })
          }
        } catch (error: any) {
          console.error('Error selling holding:', error)
          toast.error(error.message || 'Failed to sell stock')
        }
      },

      deleteHolding: async (symbol) => {
        try {
          const response = await apiFetch(`/api/portfolio/${symbol}`, {
            method: 'DELETE'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to delete stock')
          }

          await get().loadPortfolio()
          toast.success('Stock deleted successfully')
          set({ showDeleteModal: false, selectedStock: null })
        } catch (error: any) {
          console.error('Error deleting holding:', error)
          toast.error(error.message || 'Failed to delete stock')
        }
      },

      setDisplayCurrency: (currency) => {
        set({ displayCurrency: currency })
        localStorage.setItem('displayCurrency', currency)
      },

      openSellModal: (stock) => {
        set({ selectedStock: stock, showSellModal: true })
      },

      openDeleteModal: (stock) => {
        set({ selectedStock: stock, showDeleteModal: true })
      },

      openAddGoldModal: () => {
        set({ showAddGoldModal: true })
      },

      closeAddGoldModal: () => {
        set({ showAddGoldModal: false })
      },

      openCsvImportModal: () => {
        set({ showCsvImportModal: true })
      },

      closeCsvImportModal: () => {
        set({ showCsvImportModal: false })
      },

      // Custom Portfolio Actions
      loadCustomPortfolios: async () => {
        // Skip if already loaded or currently loading
        const { loadingCustomPortfolios, customPortfoliosLoaded } = get()
        if (customPortfoliosLoaded || loadingCustomPortfolios) {
          console.log('⏭️  Skipping duplicate custom portfolios load')
          return
        }

        set({ loadingCustomPortfolios: true })

        try {
          console.log('📁 Loading custom portfolios...')
          const response = await apiFetch(`/api/custom-portfolios`)
          if (!response.ok) throw new Error('Failed to fetch custom portfolios')
          const data = await response.json()
          set({ customPortfolios: data, customPortfoliosLoaded: true })
          console.log('✅ Loaded custom portfolios:', data?.length)
        } catch (error) {
          console.error('Error loading custom portfolios:', error)
          toast.error('Failed to load custom portfolios')
        } finally {
          set({ loadingCustomPortfolios: false })
        }
      },

      loadPortfolioHoldings: async (portfolioId, force = false) => {
        // Skip if already loaded or currently loading this portfolio (unless forced by mutation refresh)
        const { loadingPortfolios, portfolioHoldings } = get()
        if (!force && (portfolioHoldings[portfolioId] !== undefined || loadingPortfolios.has(portfolioId))) {
          console.log(`⏭️  Skipping duplicate load for portfolio ${portfolioId}`)
          return
        }

        // Mark as loading
        set(state => ({
          loadingPortfolios: new Set([...state.loadingPortfolios, portfolioId])
        }))

        try {
          console.log(`📥 Loading holdings for portfolio ${portfolioId}`)
          const response = await apiFetch(`/api/custom-portfolios/${portfolioId}/holdings`)
          if (!response.ok) throw new Error('Failed to fetch portfolio holdings')
          const data = await response.json()

          set(state => ({
            portfolioHoldings: {
              ...state.portfolioHoldings,
              [portfolioId]: data
            }
          }))
          console.log(`✅ Loaded holdings for portfolio ${portfolioId}`)
        } catch (error) {
          console.error('Error loading portfolio holdings:', error)
          toast.error('Failed to load portfolio holdings')
        } finally {
          // Remove from loading set
          set(state => {
            const newLoadingPortfolios = new Set(state.loadingPortfolios)
            newLoadingPortfolios.delete(portfolioId)
            return { loadingPortfolios: newLoadingPortfolios }
          })
        }
      },

      createCustomPortfolio: async (portfolio) => {
        try {
          const response = await apiFetch(`/api/custom-portfolios`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(portfolio)
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to create portfolio')
          }

          const newPortfolio = await response.json()
          
          set(state => ({
            customPortfolios: [...state.customPortfolios, newPortfolio],
            showCustomPortfolioModal: false,
            selectedPortfolio: null
          }))
          
          toast.success('Portfolio created successfully')
        } catch (error: any) {
          console.error('Error creating custom portfolio:', error)
          toast.error(error.message || 'Failed to create portfolio')
        }
      },

      updateCustomPortfolio: async (id, portfolio) => {
        try {
          const response = await apiFetch(`/api/custom-portfolios/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(portfolio)
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to update portfolio')
          }

          const updatedPortfolio = await response.json()
          
          set(state => ({
            customPortfolios: state.customPortfolios.map(p => 
              p.id === id ? updatedPortfolio : p
            ),
            showCustomPortfolioModal: false,
            selectedPortfolio: null
          }))
          
          toast.success('Portfolio updated successfully')
        } catch (error: any) {
          console.error('Error updating custom portfolio:', error)
          toast.error(error.message || 'Failed to update portfolio')
        }
      },

      deleteCustomPortfolio: async (id) => {
        try {
          const response = await apiFetch(`/api/custom-portfolios/${id}`, {
            method: 'DELETE'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to delete portfolio')
          }

          set(state => {
            const newPortfolioHoldings = { ...state.portfolioHoldings }
            delete newPortfolioHoldings[id]
            
            return {
              customPortfolios: state.customPortfolios.filter(p => p.id !== id),
              selectedPortfolioId: state.selectedPortfolioId === id ? null : state.selectedPortfolioId,
              portfolioHoldings: newPortfolioHoldings
            }
          })
          
          toast.success('Portfolio deleted successfully')
        } catch (error: any) {
          console.error('Error deleting custom portfolio:', error)
          toast.error(error.message || 'Failed to delete portfolio')
        }
      },

      assignHoldingToPortfolio: async (portfolioId, holdingId) => {
        try {
          const response = await apiFetch(`/api/custom-portfolios/${portfolioId}/holdings/${holdingId}`, {
            method: 'POST'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to assign holding')
          }

          // Refresh portfolio holdings
          await get().loadPortfolioHoldings(portfolioId, true)

          // Auto-update portfolio currency based on holdings
          const suggestedCurrency = get().detectCurrencyFromHoldings(portfolioId)
          const portfolio = get().customPortfolios.find(p => p.id === portfolioId)
          
          if (portfolio && portfolio.currency !== suggestedCurrency) {
            // Only update if current currency doesn't match suggested currency
            await get().updateCustomPortfolio(portfolioId, { currency: suggestedCurrency })
            toast.success(`Stock assigned and portfolio currency updated to ${suggestedCurrency}`)
          } else {
            toast.success('Stock assigned to portfolio')
          }
        } catch (error: any) {
          console.error('Error assigning holding to portfolio:', error)
          toast.error(error.message || 'Failed to assign stock')
        }
      },

      removeHoldingFromPortfolio: async (portfolioId, holdingId) => {
        try {
          const response = await apiFetch(`/api/custom-portfolios/${portfolioId}/holdings/${holdingId}`, {
            method: 'DELETE'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to remove holding')
          }

          // Refresh portfolio holdings
          await get().loadPortfolioHoldings(portfolioId, true)
          toast.success('Stock removed from portfolio')
        } catch (error: any) {
          console.error('Error removing holding from portfolio:', error)
          toast.error(error.message || 'Failed to remove stock')
        }
      },

      autoAssignHoldings: async () => {
        try {
          const response = await apiFetch(`/api/custom-portfolios/auto-assign`, {
            method: 'POST'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to auto-assign holdings')
          }

          // Refresh all portfolio holdings and update currencies
          const { customPortfolios } = get()
          for (const portfolio of customPortfolios) {
            await get().loadPortfolioHoldings(portfolio.id, true)
            
            // Auto-update currency based on assigned holdings
            const suggestedCurrency = get().detectCurrencyFromHoldings(portfolio.id)
            if (portfolio.currency !== suggestedCurrency) {
              await get().updateCustomPortfolio(portfolio.id, { currency: suggestedCurrency })
            }
          }
          
          toast.success('Holdings auto-assigned and currencies updated successfully')
        } catch (error: any) {
          console.error('Error auto-assigning holdings:', error)
          toast.error(error.message || 'Failed to auto-assign holdings')
        }
      },

      setSelectedPortfolio: (portfolioId) => {
        set({ selectedPortfolioId: portfolioId })
        
        // Load holdings for the selected portfolio if not already loaded
        if (portfolioId) {
          const { portfolioHoldings } = get()
          if (!portfolioHoldings[portfolioId]) {
            get().loadPortfolioHoldings(portfolioId)
          }
        }
      },

      openCustomPortfolioModal: (portfolio) => {
        set({ 
          showCustomPortfolioModal: true, 
          selectedPortfolio: portfolio || null 
        })
      },

      // Sector Actions
      loadSectors: async () => {
        const { loadingSectors, sectorsLoaded } = get()
        if (sectorsLoaded || loadingSectors) return

        set({ loadingSectors: true })
        try {
          const response = await apiFetch(`/api/sectors`)
          if (!response.ok) throw new Error('Failed to fetch sectors')
          const data = await response.json()
          set({ sectors: data, sectorsLoaded: true })
        } catch (error) {
          console.error('Error loading sectors:', error)
          toast.error('Failed to load sectors')
        } finally {
          set({ loadingSectors: false })
        }
      },

      createSector: async (sector) => {
        try {
          const response = await apiFetch(`/api/sectors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(sector)
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to create sector')
          }

          const newSector = await response.json()
          set(state => ({
            sectors: [...state.sectors, newSector]
          }))

          toast.success('Sector created successfully')
        } catch (error: any) {
          console.error('Error creating sector:', error)
          toast.error(error.message || 'Failed to create sector')
        }
      },

      updateSector: async (id, sector) => {
        try {
          const response = await apiFetch(`/api/sectors/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(sector)
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to update sector')
          }

          const updatedSector = await response.json()
          set(state => ({
            sectors: state.sectors.map(s => s.id === id ? updatedSector : s)
          }))

          toast.success('Sector updated successfully')
        } catch (error: any) {
          console.error('Error updating sector:', error)
          toast.error(error.message || 'Failed to update sector')
        }
      },

      deleteSector: async (id) => {
        try {
          const response = await apiFetch(`/api/sectors/${id}`, {
            method: 'DELETE'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to delete sector')
          }

          set(state => ({
            sectors: state.sectors.filter(s => s.id !== id)
          }))

          toast.success('Sector deleted successfully')
        } catch (error: any) {
          console.error('Error deleting sector:', error)
          toast.error(error.message || 'Failed to delete sector')
        }
      },

      getSectorByKey: (key: string) => {
        const { sectors } = get()
        return sectors.find(sector => sector.key === key)
      },

      // Country Actions
      loadCountries: async () => {
        const { loadingCountries, countriesLoaded } = get()
        if (countriesLoaded || loadingCountries) return

        set({ loadingCountries: true })
        try {
          const response = await apiFetch('/api/countries')
          if (!response.ok) throw new Error('Failed to fetch countries')
          const data = await response.json()
          set({ countries: data || [], countriesLoaded: true })
        } catch (error) {
          console.error('Error loading countries:', error)
          toast.error('Failed to load countries from database')
        } finally {
          set({ loadingCountries: false })
        }
      },

      createCountry: async (country) => {
        try {
          const response = await apiFetch('/api/countries', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(country)
          })
          if (!response.ok) throw new Error('Failed to create country')
          const newCountry = await response.json()
          set(state => ({ countries: [...state.countries, newCountry] }))
          toast.success('Country created successfully')
        } catch (error: any) {
          console.error('Error creating country:', error)
          toast.error(error.message || 'Failed to create country')
        }
      },

      updateCountry: async (id, country) => {
        try {
          const response = await apiFetch(`/api/countries/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(country)
          })
          if (!response.ok) throw new Error('Failed to update country')
          const updatedCountry = await response.json()
          set(state => ({
            countries: state.countries.map(c => c.id === id ? updatedCountry : c)
          }))
          toast.success('Country updated successfully')
        } catch (error: any) {
          console.error('Error updating country:', error)
          toast.error(error.message || 'Failed to update country')
        }
      },

      deleteCountry: async (id) => {
        try {
          const { countries, portfolio } = get()
          const existingCountry = countries.find(c => c.id === id)
          if (!existingCountry) throw new Error('Country not found')

          const isCountryInUse = portfolio.some(holding => holding.country === existingCountry.key)
          if (isCountryInUse) throw new Error('Cannot delete country that is currently assigned to stocks')

          const response = await apiFetch(`/api/countries/${id}`, { method: 'DELETE' })
          if (!response.ok) throw new Error('Failed to delete country')
          set(state => ({ countries: state.countries.filter(c => c.id !== id) }))
          toast.success('Country deleted successfully')
        } catch (error: any) {
          console.error('Error deleting country:', error)
          toast.error(error.message || 'Failed to delete country')
        }
      },

      getCountryByKey: (key: string) => {
        const { countries } = get()
        return countries.find(country => country.key === key)
      },

      // Currency Actions
      loadCurrencies: async () => {
        const { loadingCurrencies, currenciesLoaded } = get()
        if (currenciesLoaded || loadingCurrencies) return

        set({ loadingCurrencies: true })
        try {
          const response = await apiFetch(`/api/currencies`)
          if (!response.ok) throw new Error('Failed to fetch currencies')
          const data = await response.json()
          set({ currencies: data, currenciesLoaded: true })
        } catch (error) {
          console.error('Error loading currencies:', error)
          toast.error('Failed to load currencies')
        } finally {
          set({ loadingCurrencies: false })
        }
      },

      createCurrency: async (currency) => {
        try {
          const response = await apiFetch(`/api/currencies`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(currency)
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to create currency')
          }

          const newCurrency = await response.json()
          set(state => ({
            currencies: [...state.currencies, newCurrency]
          }))

          toast.success('Currency created successfully')
        } catch (error: any) {
          console.error('Error creating currency:', error)
          toast.error(error.message || 'Failed to create currency')
        }
      },

      updateCurrency: async (id, currency) => {
        try {
          const response = await apiFetch(`/api/currencies/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(currency)
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to update currency')
          }

          const updatedCurrency = await response.json()
          set(state => ({
            currencies: state.currencies.map(c => c.id === id ? updatedCurrency : c)
          }))

          toast.success('Currency updated successfully')
        } catch (error: any) {
          console.error('Error updating currency:', error)
          toast.error(error.message || 'Failed to update currency')
        }
      },

      deleteCurrency: async (id) => {
        try {
          const response = await apiFetch(`/api/currencies/${id}`, {
            method: 'DELETE'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to delete currency')
          }

          set(state => ({
            currencies: state.currencies.filter(c => c.id !== id)
          }))

          toast.success('Currency deleted successfully')
        } catch (error: any) {
          console.error('Error deleting currency:', error)
          toast.error(error.message || 'Failed to delete currency')
        }
      },

      getCurrencyByValue: (value: string) => {
        const { currencies } = get()
        return currencies.find(currency => currency.value === value)
      },

      // Computed functions
      getCurrentPrice: (symbol, currency) => {
        const { currentPrices, displayCurrency, portfolio } = get()
        
        // For Gold holdings, try to get current market price, fallback to purchase price
        if (symbol.startsWith('GOLD-')) {
          const goldHolding = portfolio.find(h => h.symbol === symbol)
          if (goldHolding) {
            // Check if we have current gold price data
            const goldPriceData = currentPrices['GOLD_CURRENT']
            let currentGoldPrice = goldPriceData?.thb || goldHolding.purchase_price
            
            const targetCurrency = currency || displayCurrency
            if (targetCurrency === 'THB') {
              return currentGoldPrice
            } else {
              // Convert to USD if needed
              return get().convertPrice(currentGoldPrice, 'THB', targetCurrency)
            }
          }
          return 0
        }
        
        const priceData = currentPrices[symbol]
        if (!priceData) return 0
        
        const targetCurrency = currency || displayCurrency
        return targetCurrency === 'USD' ? (priceData.usd || 0) : (priceData.thb || 0)
      },

      fetchGoldPrice: async () => {
        try {
          const response = await apiFetch(`/api/prices/gold`)
          if (!response.ok) throw new Error('Failed to fetch gold price')
          const data = await response.json()
          
          // Store gold price in currentPrices for future use
          const goldPriceData = {
            usd: (data.price || data.bid) / get().exchangeRate,
            thb: data.price || data.bid,
            bid: data.bid,
            ask: data.ask,
            diff: data.diff,
            exchangeRate: get().exchangeRate,
            baseCurrency: 'THB',
            timestamp: data.timestamp
          }
          
          set(state => ({
            currentPrices: {
              ...state.currentPrices,
              'GOLD_CURRENT': goldPriceData
            }
          }))
          
          return data.price || data.bid
        } catch (error) {
          console.error('Error fetching gold price:', error)
          throw error
        }
      },

      convertPrice: (price, fromCurrency, toCurrency) => {
        const { exchangeRate } = get()
        if (fromCurrency === toCurrency) return price
        
        if (fromCurrency === 'USD' && toCurrency === 'THB') {
          return price * exchangeRate
        } else if (fromCurrency === 'THB' && toCurrency === 'USD') {
          return price / exchangeRate
        }
        
        return price
      },

      calculateRealizedPnL: () => {
        const { getPortfolioCurrency, selectedPortfolioId, portfolioHoldings, exchangeRate } = get()
        const effectiveCurrency = getPortfolioCurrency()
        
        // Get trading history from trading history store (same as Trading page)
        const tradingHistory = useTradingHistoryStore.getState().tradingHistory || []
        
        // Filter trading history based on selected portfolio
        let filteredHistory = tradingHistory
        if (selectedPortfolioId) {
          const selectedHoldings = portfolioHoldings[selectedPortfolioId] || []
          const selectedSymbols = selectedHoldings.map(h => h.symbol)
          filteredHistory = tradingHistory.filter(record => 
            selectedSymbols.includes(record.symbol)
          )
        }
        
        // Helper function to convert amount to display currency (same as Trading page)
        const convertToDisplayCurrency = (amount: number, recordCurrency: string) => {
          if (!amount || isNaN(amount) || !isFinite(amount)) {
            return 0
          }
          
          if (recordCurrency === effectiveCurrency) {
            return amount
          }
          
          if (recordCurrency === 'USD' && effectiveCurrency === 'THB') {
            return amount * exchangeRate
          } else if (recordCurrency === 'THB' && effectiveCurrency === 'USD') {
            return amount / exchangeRate
          }
          
          return amount
        }
        
        // Calculate realized P&L exactly like Trading page
        const realizedPnL = filteredHistory
          .filter(record => record.action === 'SELL')
          .reduce((total, sellRecord) => {
            const sellAmount = convertToDisplayCurrency(sellRecord.total_amount, sellRecord.currency)
            const sellFees = sellRecord.fees ? convertToDisplayCurrency(sellRecord.fees, sellRecord.currency) : 0
            
            // Find corresponding buy records for this symbol to calculate cost basis
            const buyRecords = filteredHistory.filter(r => 
              r.action === 'BUY' && 
              r.symbol === sellRecord.symbol &&
              new Date(r.created_at) <= new Date(sellRecord.created_at)
            )
            
            if (buyRecords.length > 0) {
              // Use weighted average cost (same logic as Trading page)
              const totalBoughtShares = buyRecords.reduce((sum, r) => {
                const shares = r.shares || 0
                return sum + (isNaN(shares) ? 0 : shares)
              }, 0)
              const totalBoughtAmount = buyRecords.reduce((sum, r) => {
                const amount = convertToDisplayCurrency(r.total_amount, r.currency)
                return sum + (isNaN(amount) ? 0 : amount)
              }, 0)
              
              // Prevent division by zero
              if (totalBoughtShares <= 0) {
                return total
              }
              
              const avgCostPerShare = totalBoughtAmount / totalBoughtShares
              const sellShares = sellRecord.shares || 0
              
              // Check for valid numbers
              if (isNaN(avgCostPerShare) || isNaN(sellShares) || isNaN(sellAmount)) {
                return total
              }
              
              const costBasis = sellShares * avgCostPerShare
              const profit = sellAmount - sellFees - costBasis
              
              // Only add if profit is a valid number
              if (isNaN(profit) || !isFinite(profit)) {
                return total
              }
              
              return total + profit
            }
            
            return total
          }, 0)
        
        return realizedPnL
      },

      triggerDailySnapshot: async () => {
        try {
          const { calculateSummary, getPortfolioCurrency, selectedPortfolioId } = get()
          const summary = calculateSummary()
          const currency = getPortfolioCurrency()
          await portfolioHistoryService.autoSaveSnapshot(selectedPortfolioId, summary, currency)
        } catch (error) {
          console.warn('Failed to trigger daily snapshot:', error)
        }
      },

      calculateSummary: () => {
        const { getCurrentPrice, convertPrice, getFilteredPortfolio, getPortfolioCurrency, calculateRealizedPnL } = get()
        const filteredPortfolio = getFilteredPortfolio()
        const effectiveCurrency = getPortfolioCurrency()
        
        let totalValue = 0
        let totalCost = 0

        filteredPortfolio.forEach(holding => {
          const purchasePriceInDisplayCurrency = convertPrice(
            holding.purchase_price,
            holding.purchase_currency,
            effectiveCurrency
          )
          
          const cost = purchasePriceInDisplayCurrency * holding.shares
          const currentPrice = getCurrentPrice(holding.symbol, effectiveCurrency)
          const value = currentPrice * holding.shares

          totalCost += cost
          totalValue += value
        })

        const totalGainLoss = totalValue - totalCost
        const gainLossPercent = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0
        const realizedPnL = calculateRealizedPnL()

        return {
          totalValue,
          totalCost,
          totalGainLoss,
          gainLossPercent,
          realizedPnL
        }
      },

      getFilteredPortfolio: () => {
        const { portfolio, selectedPortfolioId, portfolioHoldings } = get()
        
        if (!selectedPortfolioId) {
          return portfolio
        }
        
        const selectedHoldings = portfolioHoldings[selectedPortfolioId]
        if (!selectedHoldings) {
          return []
        }
        
        // Return holdings that are in the selected portfolio
        return portfolio.filter(holding => 
          selectedHoldings.some(ph => ph.id === holding.id)
        )
      },

      getPortfolioCurrency: () => {
        const { selectedPortfolioId, customPortfolios, displayCurrency } = get()
        
        if (!selectedPortfolioId) {
          return displayCurrency
        }
        
        const selectedPortfolio = customPortfolios.find(p => p.id === selectedPortfolioId)
        return selectedPortfolio?.currency || displayCurrency
      },

      detectCurrencyFromHoldings: (portfolioId: number) => {
        const { portfolio, portfolioHoldings } = get()
        
        // Get holdings for this portfolio
        const holdingsForPortfolio = portfolioHoldings[portfolioId] || []
        const relevantHoldings = portfolio.filter(holding => 
          holdingsForPortfolio.some(ph => ph.id === holding.id)
        )
        
        if (relevantHoldings.length === 0) {
          return 'THB' // Default to THB
        }
        
        // Count holdings by type
        const typeCounts = relevantHoldings.reduce((counts, holding) => {
          counts[holding.port_api_type] = (counts[holding.port_api_type] || 0) + 1
          return counts
        }, {} as Record<string, number>)
        
        const usCount = (typeCounts['us_stock'] || 0) + (typeCounts['us_fund'] || 0)
        const thCount = (typeCounts['th_stock'] || 0) + (typeCounts['th_fund'] || 0)
        
        // If majority is US stocks, suggest USD; otherwise THB
        return usCount > thCount ? 'USD' : 'THB'
      },

      getPortfolioStats: (portfolioId: number) => {
        const { portfolio, portfolioHoldings, getCurrentPrice, convertPrice, customPortfolios } = get()
        
        // Get holdings for this portfolio
        const holdingsForPortfolio = portfolioHoldings[portfolioId] || []
        const relevantHoldings = portfolio.filter(holding => 
          holdingsForPortfolio.some(ph => ph.id === holding.id)
        )
        
        // Get portfolio currency
        const portfolioData = customPortfolios.find(p => p.id === portfolioId)
        const portfolioCurrency = portfolioData?.currency || 'THB'
        
        let totalValue = 0
        let totalCost = 0
        
        relevantHoldings.forEach(holding => {
          const purchasePriceInPortfolioCurrency = convertPrice(
            holding.purchase_price,
            holding.purchase_currency,
            portfolioCurrency
          )
          
          const cost = purchasePriceInPortfolioCurrency * holding.shares
          const currentPrice = getCurrentPrice(holding.symbol, portfolioCurrency)
          const value = currentPrice * holding.shares
          
          totalCost += cost
          totalValue += value
        })
        
        const gainLoss = totalValue - totalCost
        const gainLossPercent = totalCost > 0 ? (gainLoss / totalCost) * 100 : 0
        
        return {
          totalStocks: relevantHoldings.length,
          totalValue,
          totalCost,
          gainLoss,
          gainLossPercent
        }
      },

      updateHoldingSector: async (holdingId: number, sector: string) => {
        try {
          const response = await apiFetch(`/api/portfolio/${holdingId}/sector`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sector })
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to update sector')
          }

          // Update local state
          set(state => ({
            portfolio: state.portfolio.map(holding => 
              holding.id === holdingId ? { ...holding, sector } : holding
            )
          }))

          toast.success('Sector updated successfully')
        } catch (error: any) {
          console.error('Error updating sector:', error)
          toast.error(error.message || 'Failed to update sector')
        }
      },


      getAvailableSectors: () => {
        const commonSectors = [
          'Technology',
          'Healthcare',
          'Financial Services',
          'Consumer Discretionary',
          'Consumer Staples',
          'Energy',
          'Industrials',
          'Materials',
          'Real Estate',
          'Utilities',
          'Communication Services',
          'Cryptocurrency',
          'ETF',
          'REIT',
          'Government Bonds',
          'Corporate Bonds'
        ]

        const { portfolio } = get()
        const usedSectors = Array.from(new Set(
          portfolio
            .map(holding => holding.sector)
            .filter(sector => sector && !commonSectors.includes(sector))
        )) as string[]

        return [...commonSectors, ...usedSectors].sort()
      },

      clearDatabase: async () => {
        try {
          const response = await apiFetch(`/api/database/clear`, {
            method: 'DELETE'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to clear database')
          }

          // Clear local state (reset loaded flags so reloads proceed)
          set({
            portfolio: [],
            currentPrices: {},
            customPortfolios: [],
            customPortfoliosLoaded: false,
            selectedPortfolioId: null,
            portfolioHoldings: {},
            sectors: [],
            sectorsLoaded: false,
            countries: [],
            countriesLoaded: false,
            currencies: [],
            currenciesLoaded: false,
          })

          // Reload default data
          await Promise.all([
            get().loadCustomPortfolios(),
            get().loadSectors(),
            get().loadCountries(),
            get().loadCurrencies()
          ])

          toast.success('Database cleared successfully')
        } catch (error: any) {
          console.error('Error clearing database:', error)
          toast.error(error.message || 'Failed to clear database')
        }
      }
    }),
    {
      name: 'portfolio-store'
    }
  )
)

// Initialize display currency from localStorage
const savedCurrency = localStorage.getItem('displayCurrency') as 'USD' | 'THB'
if (savedCurrency) {
  usePortfolioStore.setState({ displayCurrency: savedCurrency })
}

// Make store available globally for utils.ts
if (typeof globalThis !== 'undefined') {
  ;(globalThis as any).portfolioStore = usePortfolioStore.getState()
  
  // Update global reference when store changes
  usePortfolioStore.subscribe((state) => {
    ;(globalThis as any).portfolioStore = state
  })
}
